import React from 'react';
import { Link } from 'react-router-dom';
import { Gift, Users, Car, DollarSign, CheckCircle, Phone } from 'lucide-react';
import useSEO from '@/hooks/useSEO';

const ReferralProgramPage = () => {
  useSEO('referral-program', 'Refer & Earn $100 | We Have Your Car');

  const steps = [
    { icon: Users, title: 'Send us a referral', description: 'Tell your friend or family about We Have Your Car' },
    { icon: CheckCircle, title: 'They mention your name', description: 'Your referral mentions you when they visit' },
    { icon: Car, title: 'They buy a car', description: 'Your referral purchases a vehicle from us' },
    { icon: DollarSign, title: 'You get paid', description: 'Receive your $100 Amazon gift card' },
  ];

  const terms = [
    'Must be a new customer (not a previous buyer)',
    'Must mention referrer before or at time of purchase',
    'One reward per completed sale',
    'No self-referrals allowed',
    'Gift card paid within 7-14 days after completed sale',
    'Program subject to change',
    'Fraud voids reward eligibility',
  ];

  return (
    <div className="min-h-screen bg-[#0a1628]">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#132238] to-[#1a4d8c]/30 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-[#ff6b35]/20 rounded-full mb-6">
            <Gift size={40} className="text-[#ff6b35]" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4 font-['Exo_2']">
            Refer a Friend. <span className="gradient-text">Get $100.</span>
          </h1>
          <p className="text-xl text-[#a8b8d0] max-w-2xl mx-auto">
            Refer someone to We Have Your Car. If they purchase a vehicle, you receive a $100 Amazon gift card.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* How It Works */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-white text-center mb-8">How It Works</h2>
          <div className="grid md:grid-cols-4 gap-6">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={index} className="text-center">
                  <div className="relative">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-[#132238] border-2 border-[#ff6b35] rounded-full mb-4">
                      <Icon size={28} className="text-[#ff6b35]" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-[#ff6b35] rounded-full flex items-center justify-center text-white font-bold text-sm">
                      {index + 1}
                    </div>
                  </div>
                  <h3 className="text-white font-semibold mb-2">{step.title}</h3>
                  <p className="text-[#a8b8d0] text-sm">{step.description}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* CTA */}
        <section className="bg-gradient-to-r from-[#132238] to-[#1a2d47] rounded-2xl p-8 text-center mb-12">
          <h2 className="text-2xl font-bold text-white mb-4">Ready to Refer?</h2>
          <p className="text-[#a8b8d0] mb-6">
            Simply have your friend mention your name when they visit We Have Your Car!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:5126660016" className="btn-primary inline-flex items-center justify-center gap-2">
              <Phone size={20} /> Call (512) 666-0016
            </a>
            <Link to="/inventory" className="btn-secondary inline-flex items-center justify-center gap-2">
              <Car size={20} /> Browse Inventory
            </Link>
          </div>
        </section>

        {/* Terms */}
        <section className="bg-[#132238] border border-[#1a2d47] rounded-xl p-6">
          <h2 className="text-xl font-bold text-white mb-4">Terms & Conditions</h2>
          <ul className="space-y-3">
            {terms.map((term, index) => (
              <li key={index} className="flex items-start gap-3 text-[#a8b8d0]">
                <CheckCircle size={18} className="text-[#ff6b35] shrink-0 mt-0.5" />
                <span>{term}</span>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
};

export default ReferralProgramPage;
